import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Grid, Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip } from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend } from 'recharts';
import { Visibility } from '@mui/icons-material';
import * as actionTypes from '../../redux/actionTypes';

const STATUS_COLORS = { Created: '#2196F3', Review: '#FF9800', Working: '#9C27B0', Closed: '#4CAF50' };
const TYPE_COLORS = { Bug: '#f44336', 'Feature Request': '#2196F3', Enhancement: '#4CAF50', Crash: '#FF5722', 'UI Problem': '#FF9800' };
const PIE_COLORS = ['#2196F3', '#FF9800', '#9C27B0', '#4CAF50'];

const StatCard = ({ title, value, color, subtitle, onClick }) => (
    <Paper onClick={onClick} sx={{ p: 3, borderRadius: 3, background: `linear-gradient(135deg, ${color}15 0%, ${color}08 100%)`, border: `1px solid ${color}30`, cursor: onClick ? 'pointer' : 'default', transition: 'all 0.3s', '&:hover': onClick ? { transform: 'translateY(-2px)', boxShadow: `0 8px 25px ${color}25` } : {} }}>
        <Typography variant="h3" sx={{ fontWeight: 700, color, mb: 0.5 }}>{value ?? '—'}</Typography>
        <Typography variant="body1" sx={{ fontWeight: 600, color: '#333', mb: 0.3 }}>{title}</Typography>
        {subtitle && <Typography variant="caption" sx={{ color: '#888' }}>{subtitle}</Typography>}
    </Paper>
);

const IssueDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const stats = useSelector(state => state.issueReducer?.issueStats);
    const activeIssues = useSelector(state => state.issueReducer?.activeIssues);

    useEffect(() => {
        dispatch({ type: actionTypes.GET_ISSUE_STATS });
        dispatch({ type: actionTypes.GET_ACTIVE_ISSUES });
    }, [dispatch]);

    const statusData = stats ? [
        { name: 'Created', value: stats.created || 0 },
        { name: 'Review', value: stats.review || 0 },
        { name: 'Working', value: stats.working || 0 },
        { name: 'Closed', value: stats.closed || 0 },
    ].filter(d => d.value > 0) : [];
    const typeData = stats?.byModule ? stats.byModule.map(m => ({ name: m._id, value: m.count })) : [];
    const monthlyData = stats?.monthly ? stats.monthly.map(m => ({ _id: `${m._id.year}-${String(m._id.month).padStart(2, '0')}`, count: m.count })) : [];

    const getStatusChip = (status) => (
        <Chip label={status} size="small" sx={{ bgcolor: STATUS_COLORS[status] || '#999', color: '#fff', fontWeight: 600, fontSize: '0.7rem' }} />
    );
    const getTypeChip = (type) => (
        <Chip label={type} size="small" variant="outlined" sx={{ borderColor: TYPE_COLORS[type] || '#999', color: TYPE_COLORS[type] || '#999', fontWeight: 500, fontSize: '0.7rem' }} />
    );

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 3 }}>
                🎯 Issue Control Dashboard
            </Typography>

            {/* Stat Cards */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="Total Issues" value={stats?.total} color="#2196F3" subtitle="All time" />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="Created" value={stats?.created || 0} color="#2196F3" subtitle="New issues" onClick={() => navigate('/issues?status=Created')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="In Review" value={stats?.review || 0} color="#FF9800" subtitle="Being reviewed" onClick={() => navigate('/issues?status=Review')} />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard title="Working" value={stats?.working || 0} color="#9C27B0" subtitle="In progress" onClick={() => navigate('/issues?status=Working')} />
                </Grid>
            </Grid>

            <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Status Distribution Pie */}
                <Grid item xs={12} md={4}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: 350 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Status Distribution</Typography>
                        {statusData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie data={statusData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                                        {statusData.map((entry, i) => <Cell key={i} fill={STATUS_COLORS[entry.name] || PIE_COLORS[i % PIE_COLORS.length]} />)}
                                    </Pie>
                                    <Legend />
                                    <ReTooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}><Typography color="text.secondary">No data available</Typography></Box>}
                    </Paper>
                </Grid>

                {/* Type Distribution Bar */}
                <Grid item xs={12} md={8}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: 350 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Issues by Module</Typography>
                        {typeData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart data={typeData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                    <YAxis allowDecimals={false} />
                                    <ReTooltip />
                                    <Bar dataKey="value" radius={[8, 8, 0, 0]} fill="#1976d2" />
                                </BarChart>
                            </ResponsiveContainer>
                        ) : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}><Typography color="text.secondary">No data available</Typography></Box>}
                    </Paper>
                </Grid>
            </Grid>

            {/* Monthly Trend */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 3, height: 350 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Monthly Trend</Typography>
                        {monthlyData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                                <LineChart data={monthlyData}>
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="_id" tick={{ fontSize: 12 }} />
                                    <YAxis allowDecimals={false} />
                                    <ReTooltip />
                                    <Line type="monotone" dataKey="count" stroke="#2196F3" strokeWidth={3} dot={{ r: 5 }} activeDot={{ r: 8 }} />
                                </LineChart>
                            </ResponsiveContainer>
                        ) : <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}><Typography color="text.secondary">No data available</Typography></Box>}
                    </Paper>
                </Grid>
            </Grid>

            {/* Active Issues Table */}
            <Paper sx={{ p: 3, borderRadius: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>Active Issues (Created / Review / Working)</Typography>
                    <Chip label={`${activeIssues?.length || 0} active`} color="primary" size="small" />
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 700 }}>Issue ID</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Module</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                                <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {activeIssues && activeIssues.length > 0 ? activeIssues.slice(0, 15).map((issue) => (
                                <TableRow key={issue._id} hover sx={{ cursor: 'pointer' }}>
                                    <TableCell sx={{ fontWeight: 600, color: '#1976d2' }}>{issue.issueId}</TableCell>
                                    <TableCell sx={{ maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{issue.title}</TableCell>
                                    <TableCell>{getTypeChip(issue.type)}</TableCell>
                                    <TableCell>{issue.module}</TableCell>
                                    <TableCell>{getStatusChip(issue.status)}</TableCell>
                                    <TableCell>{new Date(issue.createdAt).toLocaleDateString('en-IN')}</TableCell>
                                    <TableCell>
                                        <Tooltip title="View Details">
                                            <IconButton size="small" onClick={() => navigate(`/issues/${issue._id}`)} sx={{ color: '#1976d2' }}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#999' }}>No active issues found</TableCell></TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>
        </Box>
    );
};

export default IssueDashboard;
